<template>
  <v-container fluid grid-list-md>
    <AppTitleBar>
      <template slot="title">{{ current ? current.display : '...' }}</template>
      <template slot="actions">
        <v-tooltip top transition="fade-transition">
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="create" v-on="on">
              <v-icon small v-text="$vuetify.icons.values.add" />
            </v-btn>
          </template>
          <span>{{ $t('new') }}</span>
        </v-tooltip>
        <v-menu bottom left min-width="150">
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on">
              <v-icon small v-text="$vuetify.icons.values.options" />
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-if="$flow.isAllowed('update', current)"
              :to="{ name: 'flowProperties', params: $route.params }"
            >
              <v-list-item-title>{{ $t('properties') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="$flow.isAllowed('update', current)"
              :to="{ name: 'flowActionList', params: $route.params }"
            >
              <v-list-item-title>{{ $t('actions') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="archive" v-if="$flow.isAllowed('destroy', current)">
              <v-list-item-title>{{ $t('archive') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="destroy" v-if="$flow.isAllowed('destroy', current)">
              <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </AppTitleBar>
    <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
    <v-loading v-if="loading" />
    <v-row v-else>
      <v-col cols="12">
        <Container
          @drop="onDrop"
          @drag-start="(e) => log('drag start', e)"
          @drag-end="(e) => log('drag end', e)"
          :should-accept-drop="shouldAcceptDrop"
          :get-child-payload="getChildPayload"
          drag-class="card-ghost"
          drop-class="card-ghost-drop"
        >
          <Draggable v-for="item in items" :key="`flow-${item.name}`">
            <flow-state-list-item :flow-id="flowId" :item="item" :on-delete="doDeleteState" />
          </Draggable>
        </Container>
      </v-col>
    </v-row>
    <router-view></router-view>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Container, Draggable } from 'vue-smooth-dnd'
import { create } from 'vue-modal-dialogs'
import { AppTitleBar, BaseForm, FormErrors, VLoading } from '@argon/app/components'
import { NAMESPACE as REALTIME_NAMESPACE } from '@argon/realtime/store'
import { NAMESPACE } from '../store'
import {
  ConfirmDeleteFlowDialog,
  ConfirmArchiveFlowDialog,
  ConfirmDeleteStateDialog,
  FlowStateListItem
} from '../components'
import flowPerimeter from '../perimeters.js'

const confirmDeleteFlow = create(ConfirmDeleteFlowDialog)
const confirmArchiveFlow = create(ConfirmArchiveFlowDialog)
const confirmDeleteState = create(ConfirmDeleteStateDialog)

export default {
  name: 'FlowWidget',
  extends: BaseForm,
  components: {
    AppTitleBar,
    VLoading,
    FormErrors,
    Container,
    Draggable,
    FlowStateListItem
  },
  perimeters: [flowPerimeter],
  props: {
    flowId: {
      type: [String, Number],
      required: true
    },
    orgRef: {
      type: String
    }
  },
  data: () => ({ loading: true }),
  computed: {
    ...mapState(NAMESPACE, ['current']),
    items() {
      return this.current ? this.current.meta.states : []
    },
    realtimeKey() {
      return this.current
        ? this.$store.getters[`${REALTIME_NAMESPACE}/realtimeKey`]({
            category: 'flows',
            identifier: this.current.refid
          })
        : null
    }
  },
  methods: {
    ...mapActions(REALTIME_NAMESPACE, ['addListener', 'removeListener']),
    ...mapActions(NAMESPACE, [
      'applyDrag',
      'getFlow',
      'deleteFlow',
      'archiveFlows',
      'deleteState',
      'orderStates',
      'deleteTransition',
      'deleteGuard',
      'deleteAction',
      'clearFlow'
    ]),
    goToList() {
      this.$router.push({ name: 'flows' })
    },
    create() {
      let params = { ...this.$route.params, stateName: 'new' }
      this.$router.push({ name: 'flowState', params })
    },
    archive() {
      return confirmArchiveFlow().then((res) => {
        if (res) {
          this.archiveFlows([this.current.uris.self]).then(() => {
            this.goToList()
          })
        }
      })
    },
    goTo(state) {
      let params = { ...this.$route.params, stateName: state.name }
      this.$router.push({ name: 'flowState', params })
    },
    destroy() {
      return confirmDeleteFlow().then((res) => {
        if (res) {
          this.deleteFlow(this.flowId).then(() => {
            this.goToList()
          })
        }
      })
    },
    doDeleteState(data) {
      return confirmDeleteState().then((res) => {
        if (res) {
          this.resetServerErrors()
          let payload = { flowId: this.flowId, data }
          return this.deleteState(payload).catch(this.setServerErrors)
        }
      })
    },
    doSortStates(data) {
      this.applyDrag({ ...data, metaItem: 'states' }).then((items) => {
        let payload = { flowId: this.flowId, items }
        return this.orderStates(payload)
      })
    },
    shouldAcceptDrop(sourceContainerOptions, payload) {
      return true
    },
    onDrop(dropResult) {
      this.doSortStates({ items: this.items, dropResult })
    },
    getChildPayload(index) {},
    log(val) {},
    fetchData() {
      return this.getFlow(this.flowId)
        .catch((error) => {
          if (error.status_code === 404) {
            this.$router.push({ name: 404 })
          }
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    realtimeKey: function (v, oldV) {
      if (oldV) {
        this.fetchData()
      }
    }
  },
  created() {
    this.fetchData().then(() => {
      this.addListener({
        category: 'flows',
        identifier: this.current.refid
      })
    })
  },
  destroyed() {
    this.removeListener({ category: 'flows', identifier: this.current.refid })
    this.clearFlow()
  }
}
</script>

<style scoped lang="scss">
.drag-content {
  padding: 0.4em;
}

.card-ghost {
  transition: transform 0.18s ease;
  transform: rotateZ(2deg);
}

.card-ghost-drop {
  transition: transform 0.18s ease-in-out;
  transform: rotateZ(0deg);
}

.opacity-ghost {
  transition: all 0.18s ease;
  opacity: 0.8;
  /* transform: rotateZ(5deg); */
  background-color: cornflowerblue;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0.3);
}

.opacity-ghost-drop {
  opacity: 1;
  /* transform: rotateZ(0deg); */
  background-color: white;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0);
}
</style>

<i18n>
{
  "en": {
    "actions": "@:labels.actions",
    "archive": "@:labels.archive",
    "delete": "@:labels.delete",
    "new": "New State",
    "properties": "@:labels.properties"
  },
  "fr": {
    "actions": "@:labels.actions",
    "archive": "@:labels.archive",
    "delete": "@:labels.delete",
    "new": "Nouvel état",
    "properties": "@:labels.properties"
  }
}
</i18n>
